import { useEffect, useState } from 'react';
import { HorizontalBarChart } from '../../components/dashboard/HorizontalBarChart';
import { StatCard } from '../../components/dashboard/StatCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { fetchGtmAnalytics, GtmAnalytics } from '../../services/gtmAnalyticsApi';

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatPercent(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

export function GtmAnalyticsPage() {
  const [analytics, setAnalytics] = useState<GtmAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(false);
      try {
        setAnalytics(await fetchGtmAnalytics());
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <LoadingSpinner label="Carregando analytics GTM..." />;
  }

  if (error || !analytics) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
        Não foi possível carregar o analytics GTM. Tente novamente em instantes.
      </div>
    );
  }

  const { summary } = analytics;

  return (
    <PageShell variant="dashboard">
      <PageHeader
        title="Analytics GTM"
        description={`ROI, campanhas e LTV por produto — atualizado em ${new Date(analytics.generatedAtUtc).toLocaleString('pt-BR')}`}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Prospects" value={formatNumber(summary.totalProspects)} />
        <StatCard label="Oportunidades" value={formatNumber(summary.totalOpportunities)} />
        <StatCard label="Receita ganha" value={formatCurrency(summary.wonRevenue)} />
        <StatCard label="Pipeline aberto" value={formatCurrency(summary.pipelineValue)} />
        <StatCard
          label="Conversão prospect → oportunidade"
          value={formatPercent(summary.prospectToOpportunityRate)}
        />
        <StatCard label="Win rate" value={formatPercent(summary.opportunityWinRate)} />
        <StatCard label="Oportunidades ganhas" value={formatNumber(summary.wonOpportunities)} />
      </div>

      <section className="mt-6 grid gap-4 xl:grid-cols-2">
        <HorizontalBarChart
          title="Receita por produto"
          items={analytics.byProduct.map((item) => ({
            label: item.name,
            value: item.wonRevenue,
            count: item.opportunityCount,
          }))}
          valueFormatter={formatCurrency}
        />

        <HorizontalBarChart
          title="LTV estimado por produto"
          items={analytics.byProduct.map((item) => ({
            label: item.name,
            value: item.estimatedLtv,
            count: item.wonCount,
          }))}
          valueFormatter={formatCurrency}
        />

        <HorizontalBarChart
          title="Oportunidades por segmento de mercado"
          items={analytics.byMarketSegment.map((item) => ({
            label: item.name,
            value: item.wonRevenue,
            count: item.opportunityCount,
          }))}
          valueFormatter={formatCurrency}
        />

        <HorizontalBarChart
          title="Campanhas (prospects)"
          items={analytics.byCampaign.map((item) => ({
            label: item.campaignTag,
            value: item.prospectCount,
            count: item.opportunityCount,
          }))}
          valueFormatter={formatNumber}
        />
      </section>

      {analytics.byBuyerPersona.length > 0 ? (
        <section className="mt-6">
          <div className="erp-card">
            <div className="erp-card-body">
              <h2 className="erp-card-title">Personas compradoras</h2>
              <div className="overflow-x-auto">
                <table className="erp-table w-full">
                  <thead>
                    <tr>
                      <th>Persona</th>
                      <th>Prospects</th>
                      <th>Oportunidades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.byBuyerPersona.map((item) => (
                      <tr key={item.buyerPersonaId}>
                        <td>{item.name}</td>
                        <td>{formatNumber(item.prospectCount)}</td>
                        <td>{formatNumber(item.opportunityCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </PageShell>
  );
}
